import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, BadgeCheck, Eye, FlaskConical, Gauge, ExternalLink, MessageSquare, Info } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getAiVisibility } from "@/lib/api";

// How each observation type is presented — the whole point is honesty about certainty.
const TYPE_META = {
  verified: { label: "Verified", icon: BadgeCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", note: "Confirmed citation with a link." },
  observed: { label: "Observed", icon: Eye, cls: "bg-blue-50 text-brand border-blue-200", note: "Seen at least once, not guaranteed to repeat." },
  simulated: { label: "Simulated", icon: FlaskConical, cls: "bg-violet-50 text-violet-700 border-violet-200", note: "Result from a manual test prompt." },
  estimate: { label: "Estimate", icon: Gauge, cls: "bg-amber-50 text-amber-700 border-amber-200", note: "Best guess from entity signals — not measured." },
};
const STATUS_CLS = { cited: "text-emerald-700", mentioned: "text-brand", absent: "text-slate-400" };

export default function AiVisibility() {
  const [data, setData] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0); document.title = "AI-Search Visibility Center | WebsiteTech.pro";
    getAiVisibility().then(setData).catch(() => setData({ observations: [], summary: {} }));
  }, []);

  const s = data?.summary || {};
  return (
    <>
      <Navbar />
      <main data-testid="ai-visibility-page" className="bg-slate-50">
        <section className="pt-28 pb-10 bg-white border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand"><Sparkles size={13} /> AI-Search Visibility</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-4">Are AI answers <span className="text-brand">mentioning you?</span></h1>
            <p className="text-slate-500 mt-4 text-lg max-w-2xl">Track how your brand shows up across AI answer engines like ChatGPT, Perplexity, Gemini and AI Overviews — with every result clearly labeled by how certain it is.</p>
          </div>
        </section>

        {/* Summary */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="aivis-summary">
            {[{ k: "cited", label: "Citations", v: s.cited, cls: "text-emerald-700" }, { k: "mentioned", label: "Mentions", v: s.mentioned, cls: "text-brand" }, { k: "absent", label: "Not found", v: s.absent, cls: "text-slate-400" }, { k: "total", label: "Observations", v: s.total, cls: "text-slate-900" }].map((c) => (
              <div key={c.k} className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
                <div className={`text-3xl font-bold ${c.cls}`}>{c.v ?? "—"}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">{c.label}</div>
              </div>
            ))}
          </div>

          {/* Certainty legend */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="aivis-legend">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3"><Info size={15} className="text-brand" /> We never blur the line between certain and uncertain</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(TYPE_META).map(([k, m]) => (
                <div key={k} className={`rounded-xl border p-3 ${m.cls}`}>
                  <div className="flex items-center gap-1.5 font-semibold text-sm"><m.icon size={14} /> {m.label} <span className="opacity-70">· {s.by_type?.[k] ?? 0}</span></div>
                  <p className="text-xs mt-1 opacity-80">{m.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Observations table */}
          <h2 className="font-heading text-2xl font-extrabold text-slate-900 mt-12 mb-4">Observations</h2>
          <div className="space-y-3" data-testid="aivis-observations">
            {(data?.observations || []).map((o, i) => {
              const m = TYPE_META[o.type] || TYPE_META.estimate;
              return (
                <motion.div key={o.id || i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  className="bg-white border border-slate-200 rounded-xl card-shadow p-4" data-testid={`aivis-row-${i}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-400">{o.engine}</div>
                      <div className="font-semibold text-slate-900">“{o.query}”</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>
                      <span className={`text-xs font-semibold capitalize ${STATUS_CLS[o.status] || "text-slate-500"}`}>{o.status}</span>
                    </div>
                  </div>
                  {o.note && <p className="text-sm text-slate-500 mt-2">{o.note}</p>}
                  {o.source_url && <a href={o.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-brand mt-2">Source <ExternalLink size={11} /></a>}
                </motion.div>
              );
            })}
          </div>

          <p className="text-xs text-slate-400 mt-6 max-w-3xl">We monitor and report visibility signals and recommend content and structured-data improvements. We do <strong>not</strong> — and cannot — force any AI platform to cite or recommend a business. Simulated and estimated results are clearly labeled and should not be read as guarantees.</p>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl p-10 md:p-14 text-center bg-brand text-white">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Improve your AI-search readiness</h2>
              <p className="mt-3 text-white/80 max-w-xl mx-auto">Start with a free audit — we'll check your structured data, entity signals and citation-friendly content.</p>
              <div className="flex flex-wrap justify-center gap-3 mt-7">
                <Link to="/audit" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-white text-brand hover:-translate-y-0.5 transition-transform">Get my free audit <ArrowRight size={16} /></Link>
                <Link to="/ai-chatbot" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 border border-white/40 hover:bg-white/10 transition-colors"><MessageSquare size={16} /> Talk to WebsiteTech AI</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
